import React, { useEffect, useRef, useState } from "react";
import { Plus, X, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button, Textarea, Spinner } from "@/components/kit";
import { AuthImage } from "@/components/AuthImage";
import { api, formatApiError } from "@/lib/api";

const MAX_PHOTOS = 6;

export default function PhotosPromptsStep({ reference, onNext, onBack, editMode }) {
  const [photos, setPhotos] = useState([]);
  const [answers, setAnswers] = useState({}); // prompt_id -> answer
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [p, a] = await Promise.all([api.get("/photos"), api.get("/prompt-answers")]);
    setPhotos(p.data);
    const map = {};
    a.data.forEach((x) => { map[x.prompt_id] = x.answer; });
    setAnswers(map);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/photos");
      setPhotos(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (id) => {
    await api.delete(`/photos/${id}`);
    const { data } = await api.get("/photos");
    setPhotos(data);
  };

  const makePrimary = async (id) => {
    await api.put(`/photos/${id}/primary`);
    const { data } = await api.get("/photos");
    setPhotos(data);
  };

  const answeredCount = Object.values(answers).filter((v) => v && v.trim()).length;

  const setAnswer = (pid, val) => setAnswers((a) => ({ ...a, [pid]: val }));

  const saveAnswer = async (pid, val) => {
    const v = (val || "").trim();
    try {
      if (!v) { await api.delete(`/prompt-answers/${pid}`); }
      else { await api.put("/prompt-answers", { prompt_id: pid, answer: v }); }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const proceed = async () => {
    if (photos.length < 1) { toast.error("Please add at least one photo."); return; }
    if (answeredCount < 2) { toast.error("Please answer at least two prompts."); return; }
    // Persist all non-empty answers before moving on
    for (const [pid, val] of Object.entries(answers)) {
      if (val && val.trim()) await saveAnswer(pid, val);
    }
    onNext();
  };

  if (loading) return <div className="flex justify-center py-10"><Spinner /></div>;

  return (
    <div className="space-y-8 animate-rise">
      <section>
        <h3 className="font-sans font-semibold text-[15px] text-[#1F2B26]">Photos</h3>
        <p className="font-sans text-sm text-[#5F6D66] mt-1">Add up to six. Your first photo is shown primary — tap the star to change it. At least one is required.</p>
        <div className="grid grid-cols-3 gap-3 mt-4" data-testid="photos-grid">
          {photos.map((p) => (
            <div key={p.id} className="relative aspect-[3/4] rounded-xl overflow-hidden border border-[#E3DCD0] bg-[#F1EEE7] group">
              <AuthImage photoId={p.id} className="h-full w-full object-cover" />
              <button onClick={() => removePhoto(p.id)} className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/45 flex items-center justify-center" data-testid={`photo-delete-${p.id}`}>
                <X className="h-3.5 w-3.5 text-white" />
              </button>
              <button onClick={() => makePrimary(p.id)} className={`absolute bottom-1.5 left-1.5 h-6 w-6 rounded-full flex items-center justify-center ${p.is_primary ? "bg-[#253B35]" : "bg-black/45"}`} data-testid={`photo-primary-${p.id}`}>
                <Star className="h-3.5 w-3.5 text-white" fill={p.is_primary ? "white" : "none"} />
              </button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <button onClick={pick} disabled={uploading} className="aspect-[3/4] rounded-xl border border-dashed border-[#8B9D90] bg-white/60 flex flex-col items-center justify-center gap-1 text-[#8B9D90]" data-testid="photo-add-btn">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-6 w-6" strokeWidth={1.6} />}
              <span className="font-sans text-[11px]">{uploading ? "Uploading" : "Add"}</span>
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="photo-file-input" />
      </section>

      <section>
        <h3 className="font-sans font-semibold text-[15px] text-[#1F2B26]">Prompts</h3>
        <p className="font-sans text-sm text-[#5F6D66] mt-1">Answer at least two. These give others a sincere glimpse of you. ({answeredCount} answered)</p>
        <div className="space-y-4 mt-4">
          {reference.prompts.map((prompt) => (
            <div key={prompt.id} className="rounded-2xl bg-white border border-[#E3DCD0] p-4">
              <label className="font-serif italic text-lg text-[#253B35]">{prompt.text}</label>
              <Textarea
                rows={2}
                maxLength={300}
                className="mt-2"
                value={answers[prompt.id] || ""}
                onChange={(e) => setAnswer(prompt.id, e.target.value)}
                onBlur={(e) => saveAnswer(prompt.id, e.target.value)}
                placeholder="Your words…"
                data-testid={`prompt-answer-${prompt.id}`}
              />
            </div>
          ))}
        </div>
      </section>

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onBack} data-testid="photos-back-btn">Back</Button>
        <Button className="flex-1" size="lg" onClick={proceed} data-testid="photos-continue-btn">Continue</Button>
      </div>
    </div>
  );
}
